import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Box,
  Typography,
} from '@mui/material';
import { colors } from '../theme/modernTheme';

/**
 * Reusable donation confirmation wrapper dialog
 * Used when actions affect both reminders and donations
 *
 * @param {boolean} open - Dialog open state
 * @param {Function} onClose - Handler to close dialog
 * @param {Function} onConfirm - Handler for confirmation
 * @param {string} title - Dialog title
 * @param {string} message - Main message
 * @param {string} warningMessage - Warning message about donation impact
 * @param {boolean} loading - Loading state during action
 */
export default function DonationConfirmationWrapper({
  open,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  warningMessage = 'This action will also affect the associated donation.',
  loading = false,
}) {
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      PaperProps={{
        sx: {
          borderRadius: '4px',
          minWidth: '400px',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          color: colors.text.primary,
        }}
      >
        {title}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ color: colors.text.secondary, mb: 2 }}>
          {message}
        </DialogContentText>
        {warningMessage && (
          <Box
            sx={{
              p: 2,
              backgroundColor: `${colors.warning?.main || '#ff9800'}15`,
              borderRadius: '4px',
              borderLeft: `4px solid ${colors.warning?.main || '#ff9800'}`,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: colors.text.primary,
                fontWeight: 500,
              }}
            >
              ⚠️ {warningMessage}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            textTransform: 'none',
            color: colors.text.secondary,
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: colors.action.hover,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          disabled={loading}
          variant="contained"
          sx={{
            textTransform: 'none',
            backgroundColor: colors.primary.main,
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: colors.primary.dark,
            },
          }}
        >
          {loading ? 'Processing...' : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
